import React, { ReactElement } from 'react';
import {
  Box,
  Checkbox,
  FormField,
  Grid,
  Header,
  Input,
  Select,
  SpaceBetween,
} from '@amzn/awsui-components-react-v3';
import Container from '@amzn/awsui-components-react-v3/polaris/container/internal';
import { useTranslation } from 'react-i18next';
import { nonUpdatableFormFields, OrderTypeOptions } from '../config';
import { FormikState } from './schema';
import { fixSiteCode } from '../../../../utils';

export default function OrderInformation({
  form,
  isEditForm,
}: {
  form: FormikState;
  isEditForm: boolean;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <Container
      header={
        <Header
          variant="h2"
          description=""
          actions={
            <span>
              <i className="red-text">*</i> {t('Required field')}
            </span>
          }
        >
          {t('Order Information')}
        </Header>
      }
    >
      <SpaceBetween direction="vertical" size="m">
        <FormField
          description=""
          label={
            <span>
              {t('Site')}
              <i className="red-text">*</i>
            </span>
          }
          errorText={form.touched.site && form.errors.site}
        >
          <Input
            data-test-id="input-site"
            value={form.values.site}
            onBlur={() => {
              form.setFieldValue('site', fixSiteCode(form.values.site));
            }}
            onChange={(event) => {
              form.setFieldValue('site', event.detail.value);
              form.setFieldTouched('site');
            }}
            placeholder={t('Enter Site name')}
          />
        </FormField>
        <FormField
          description=""
          label={t('Shipment ID')}
          errorText={form.touched.shipmentReferenceId && form.errors.shipmentReferenceId}
        >
          <Input
            disabled={isEditForm && nonUpdatableFormFields.includes('shipmentReferenceNumber')}
            data-test-id="input-shipment-reference-id"
            value={form.values.shipmentReferenceId}
            onBlur={() => {
              form.setFieldTouched('shipmentReferenceId');
            }}
            onChange={(event) => {
              form.setFieldValue('shipmentReferenceId', event.detail.value);
            }}
            placeholder={t('Scan or Enter shipment ID')}
          />
        </FormField>
        <FormField
          description=""
          label={
            <span>
              {t('Order Type')}
              <i className="red-text">*</i>
            </span>
          }
          errorText={form.touched.orderType && form.errors.orderType}
        >
          <Select
            disabled={isEditForm && nonUpdatableFormFields.includes('orderType')}
            data-test-id="input-order-type"
            selectedOption={form.values.orderType}
            onBlur={() => {
              form.setFieldTouched('orderType');
              form.setFieldTouched('orderNumber');
            }}
            onChange={(event) => {
              form.setFieldValue('orderType', event.detail.selectedOption);
            }}
            options={OrderTypeOptions}
            selectedAriaLabel="Selected"
            placeholder={t('Select an Order Type')}
          />
        </FormField>
        <FormField
          description=""
          label={
            <span>
              {t('Order Number')}
              <i className="red-text">*</i>
            </span>
          }
          errorText={form.touched.orderNumber && form.errors.orderNumber}
        >
          <Input
            disabled={isEditForm && nonUpdatableFormFields.includes('orderNumber')}
            data-test-id="input-order-number"
            value={form.values.orderNumber}
            onBlur={() => {
              form.setFieldTouched('orderType');
              form.setFieldTouched('orderNumber');
            }}
            onChange={(event) => {
              form.setFieldValue('orderNumber', event.detail.value);
            }}
            placeholder={t('Scan or Enter order number')}
          />
        </FormField>
        <FormField
          description=""
          label={<span>{t('Tracking or Bill of Lading (BOL) number')}</span>}
          errorText={form.touched.trackingNumber && form.errors.trackingNumber}
        >
          <Input
            data-test-id="input-tracking-number"
            value={form.values.trackingNumber}
            onBlur={() => {
              form.setFieldTouched('trackingNumber');
            }}
            onChange={(event) => {
              form.setFieldValue('trackingNumber', event.detail.value);
            }}
            placeholder={t('Enter tracking or bill of lading (BOL) number')}
          />
        </FormField>
        <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
          <div>
            <FormField
              description=""
              label={t('Order Line Number')}
              errorText={form.touched.orderLineNumber && form.errors.orderLineNumber}
            >
              <Input
                type="number"
                data-test-id="input-order-line-number"
                disabled={
                  form.values.entireOrder ||
                  (isEditForm && nonUpdatableFormFields.includes('orderLineNumber'))
                }
                value={`${form.values.orderLineNumber}`}
                onBlur={() => {
                  form.setFieldTouched('orderLineNumber');
                }}
                onChange={(event) => {
                  form.setFieldValue('orderLineNumber', event.detail.value);
                }}
                placeholder={t('Enter order line number')}
              />
            </FormField>
          </div>
          <div>
            <Box margin={{ top: 'xl' }}>
              <FormField
                description=""
                label=""
                errorText={form.touched.entireOrder && form.errors.entireOrder}
              >
                <Checkbox
                  disabled={isEditForm && nonUpdatableFormFields.includes('entireOrder')}
                  data-test-id="input-entire-order"
                  onChange={(event) => {
                    form.setFieldValue('entireOrder', event.detail.checked);
                    form.setFieldValue('orderLineNumber', null);
                    form.setFieldTouched('entireOrder');
                  }}
                  onBlur={() => {
                    form.setFieldTouched('orderLineNumber');
                  }}
                  checked={form.values.entireOrder}
                >
                  {t('Use Entire Order')}
                </Checkbox>
              </FormField>
            </Box>
          </div>
        </Grid>
      </SpaceBetween>
    </Container>
  );
}
